/**
 * src/lib/voice/audioSession.js — Phase 90-05
 *
 * Pure audio-session monitor. Surfaces four classes of browser/OS events that
 * matter to a walk-and-talk voice session:
 *
 *   - interrupt   — AudioContext suspended/interrupted (iOS phone call, Siri)
 *   - resume      — AudioContext back to running
 *   - routeChange — mediaDevices devicechange (AirPods connect/disconnect)
 *   - streamLost  — MediaStreamTrack 'ended' (permission revoked, OS kill)
 *   - hidden      — document.hidden flips true (tab/app background, lock)
 *   - visible     — document.hidden flips false
 *
 * All dependencies are injectable via `deps` so the module is testable in
 * jsdom without polyfilling Web Audio or MediaDevices. Real browser globals
 * are only consulted as fallback when deps are not provided.
 */

export function createAudioSessionMonitor({
  onInterrupt,
  onResume,
  onRouteChange,
  onStreamLost,
  onHidden,
  onVisible,
  deps = {},
} = {}) {
  const doc = deps.document ?? (typeof document !== 'undefined' ? document : null);
  const nav = deps.navigator ?? (typeof navigator !== 'undefined' ? navigator : null);
  const ac = deps.audioContext ?? null;
  const stream = deps.stream ?? null;
  const unsubs = [];

  if (doc && typeof doc.addEventListener === 'function') {
    const handler = () => {
      if (doc.hidden) onHidden?.();
      else onVisible?.();
    };
    doc.addEventListener('visibilitychange', handler);
    unsubs.push(() => doc.removeEventListener('visibilitychange', handler));
  }

  if (nav?.mediaDevices && typeof nav.mediaDevices.addEventListener === 'function') {
    const handler = () => onRouteChange?.();
    nav.mediaDevices.addEventListener('devicechange', handler);
    unsubs.push(() => nav.mediaDevices.removeEventListener('devicechange', handler));
  }

  if (stream && typeof stream.getAudioTracks === 'function') {
    const tracks = stream.getAudioTracks() || [];
    for (const track of tracks) {
      if (!track || typeof track.addEventListener !== 'function') continue;
      const handler = () => onStreamLost?.(track);
      track.addEventListener('ended', handler);
      unsubs.push(() => {
        if (typeof track.removeEventListener === 'function') {
          track.removeEventListener('ended', handler);
        }
      });
    }
  }

  if (ac && typeof ac.addEventListener === 'function') {
    const handler = () => {
      const state = ac.state;
      if (state === 'interrupted' || state === 'suspended') onInterrupt?.(state);
      else if (state === 'running') onResume?.();
    };
    ac.addEventListener('statechange', handler);
    unsubs.push(() => ac.removeEventListener('statechange', handler));
  }

  return {
    stop() {
      for (const u of unsubs) {
        try {
          u();
        } catch {
          // noop — monitor stop must never throw
        }
      }
      unsubs.length = 0;
    },
  };
}
